import React from "react";
import { Typography, Box, Modal, Container, Chip, Button, Link } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import ProfilePicture from "bundles/common/components/ProfilePicture";
import ApplicationDialog from "bundles/Candidate/components/ApplicationDialog";
import AuthenticationModal from "bundles/Candidate/components/AuthenticationModal";
import HTML from "bundles/common/components/HTML";
import JobOfferMediaGallery from "bundles/common/components/JobOfferMediaGallery";
import SkillView from "bundles/Recruiter/components/SkillView";
import JobOfferShareButtons from "bundles/common/components/JobOfferShareButtons";
import { formatJobOfferLocations } from "bundles/common/utils/utils.js";
import LikeIcon from "icons/like-icon.svg";

const PublicJobOfferSummaryDesktop = ({
  jobOffer,
  updateJobOffer,
  handleApply,
  isEasyApplying,
  candidateApplication,
  publicJobUrl,
  isLoginModalOpened,
  isApplicationModalOpened,
  setIsApplicationModalOpened,
  setIsLoginModalOpened,
  loginModalMode,
  handleLikeClick,
}) => {
  const i18nLocale = useSelector(state => state.i18nLocale);
  const intl = useIntl();

  const getCategories = () => {
    if (!Array.isArray(jobOffer?.categories) || jobOffer.categories.length === 0) return [];
    return jobOffer.categories.map((slug) => {
      const fallback = slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      return intl.formatMessage({ id: `categories.${slug}`, defaultMessage: fallback });
    });
  };

  const getJobStatuses = () => {
    if (!jobOffer?.job_statuses?.length) return [];
    return jobOffer.job_statuses.map(status =>
      intl.formatMessage({ id: `candidate.status_map.${status}`, defaultMessage: status })
    );
  };

  const getLanguages = () => {
    const langs = jobOffer?.offer_languages?.length
      ? jobOffer.offer_languages
      : jobOffer?.offer_language
        ? [jobOffer.offer_language]
        : [];
    return langs.map(lang =>
      intl.formatMessage({ id: `languages.${lang}`, defaultMessage: lang })
    );
  };

  const formatSkillFallback = (value) =>
    String(value || "")
      .replace(/_/g, " ")
      .replace(/(^|\s)(\p{L})/gu, (_, space, letter) => `${space}${letter.toUpperCase()}`);

  const getSkillLabel = (skill, category) => {
    const rawName = skill?.name ?? skill;
    const normalizedName = String(rawName || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    return intl.formatMessage({
      id: `skills_list.${category}.${normalizedName}`,
      defaultMessage: formatSkillFallback(rawName),
    });
  };

  const getPostedAgo = () => {
    if (!jobOffer?.published_at) return null;
    const diffMs = Date.now() - new Date(jobOffer.published_at).getTime();
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    return intl.formatMessage(
      { id: "job_offer.posted_n_days_ago" },
      { days }
    );
  };

  const categories = getCategories();
  const statuses = getJobStatuses();
  const languages = getLanguages();
  const postedAgo = getPostedAgo();

  const chipColors = ["#023331", "#166d4c", "white"];
  const chipTextColors = ["white", "white", "#023331"];
  const chipBorders = [false, false, true];

  const renderAdvantageChips = (items, keyPrefix, startIndex = 0) =>
    (items || []).map((value, index) => {
      const colorIdx = (startIndex + index) % 3;
      return (
        <Chip
          key={`${keyPrefix}-${index}`}
          label={<FormattedMessage id={`job_offer_edit.${keyPrefix}.${value}`} />}
          size="small"
          sx={{
            fontFamily: "Segoe UI Regular, sans-serif",
            fontSize: "14px",
            height: "24px",
            borderRadius: "20px",
            backgroundColor: chipColors[colorIdx],
            color: chipTextColors[colorIdx],
            border: chipBorders[colorIdx] ? "1px solid #166d4c" : "none",
            px: 1.5,
            "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", fontWeight: 400, padding: 0 },
          }}
        />
      );
    });

  const hasAdvantages =
    jobOffer?.offer_advantages?.advantages?.length > 0 ||
    jobOffer?.offer_advantages?.schedules?.length > 0 ||
    jobOffer?.offer_advantages?.environments?.length > 0 ||
    jobOffer?.offer_advantages?.primes?.length > 0;

  const advCount0 = jobOffer?.offer_advantages?.advantages?.length || 0;
  const advCount1 = advCount0 + (jobOffer?.offer_advantages?.schedules?.length || 0);
  const advCount2 = advCount1 + (jobOffer?.offer_advantages?.environments?.length || 0);

  return (
    <Box sx={{ backgroundColor: "#F1F1F1", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

          {/* JobTitle Card */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "10px",
              p: 2.5,
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2.5,
              alignItems: "start"
            }}
          >
            <Box sx={{ display: "flex", gap: 3, alignItems: "start" }}>
              <a href={`/${i18nLocale}/companies/${jobOffer?.company?.encoded_name}/${jobOffer?.company?.id}`} style={{ textDecoration: "none", flexShrink: 0 }}>
                <ProfilePicture
                  picture={jobOffer?.company?.logo_url}
                  picturesmall={jobOffer?.company?.small_logo_url}
                  size={60}
                  initials={jobOffer?.company?.name?.[0]}
                  alt={`Logo ${jobOffer?.company?.name}`}
                />
              </a>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Semibold, sans-serif",
                    fontSize: "20px",
                    lineHeight: 1.334,
                    color: "#023331",
                    mb: 0.5
                  }}
                >
                  {jobOffer?.job_title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Regular, sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#023331",
                    mb: 0.25,
                    letterSpacing: "-0.3333px"
                  }}
                >
                  {jobOffer?.company?.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Semibold, sans-serif",
                    fontSize: "14px",
                    color: "#023331",
                    letterSpacing: "-0.3333px"
                  }}
                >
                  {formatJobOfferLocations(jobOffer?.locations || []).join(" - ")}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, alignItems: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={isEasyApplying}
                sx={{
                  backgroundColor: "#023331",
                  color: "#F1F1F1",
                  borderRadius: "29px",
                  px: 23,
                  py: 1.5,
                  fontSize: "14px",
                  fontFamily: "Segoe UI Regular, sans-serif",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#012020" }
                }}
              >
                <FormattedMessage id="job_search.apply_to_job" />
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <JobOfferShareButtons
                  publicJobUrl={publicJobUrl}
                  jobTitle={jobOffer?.job_title}
                  variant="public"
                />
                <Box
                  component="img"
                  src={LikeIcon}
                  alt="Like"
                  onClick={handleLikeClick}
                  sx={{ width: "32px", height: "32px", cursor: "pointer", flexShrink: 0 }}
                />
              </Box>
            </Box>
          </Box>

          {/* JobDescription Card */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "10px",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2.5
            }}
          >
            <Box sx={{ display: "flex", gap: 3, alignItems: "start" }}>
              <a href={`/${i18nLocale}/companies/${jobOffer?.company?.encoded_name}/${jobOffer?.company?.id}`} style={{ textDecoration: "none", flexShrink: 0 }}>
                <ProfilePicture
                  picture={jobOffer?.company?.logo_url}
                  picturesmall={jobOffer?.company?.small_logo_url}
                  size={60}
                  initials={jobOffer?.company?.name?.[0]}
                  alt={`Logo ${jobOffer?.company?.name}`}
                />
              </a>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Semibold, sans-serif",
                    fontSize: "20px",
                    lineHeight: 1.334,
                    color: "#023331",
                    mb: 0.5
                  }}
                >
                  {jobOffer?.company?.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Semibold, sans-serif",
                    fontSize: "14px",
                    color: "#023331",
                    letterSpacing: "-0.3333px"
                  }}
                >
                  {formatJobOfferLocations(jobOffer?.locations || []).join(" - ")}
                </Typography>
              </Box>
            </Box>

            {/* Tags */}
            {(categories.length > 0 || statuses.length > 0 || languages.length > 0) && (
              <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                {categories.map((category, i) => (
                  <Chip
                    key={`category-${i}`}
                    label={category}
                    size="small"
                    sx={{
                      fontFamily: "Segoe UI Regular, sans-serif",
                      fontSize: "14px",
                      height: "24px",
                      borderRadius: "20px",
                      backgroundColor: "#023331",
                      color: "white",
                      px: 1.5,
                      "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", padding: 0 },
                    }}
                  />
                ))}
                {statuses.map((status, i) => (
                  <Chip
                    key={`status-${i}`}
                    label={status}
                    size="small"
                    sx={{
                      fontFamily: "Segoe UI Regular, sans-serif",
                      fontSize: "14px",
                      height: "24px",
                      borderRadius: "20px",
                      backgroundColor: "#166d4c",
                      color: "white",
                      fontWeight: 400,
                      px: 1.5,
                      "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", fontWeight: 400, padding: 0 },
                    }}
                  />
                ))}
                {languages.map((lang, i) => (
                  <Chip
                    key={`lang-${i}`}
                    label={lang}
                    size="small"
                    sx={{
                      fontFamily: "Segoe UI Regular, sans-serif",
                      fontSize: "14px",
                      height: "24px",
                      borderRadius: "20px",
                      backgroundColor: "white",
                      color: "#023331",
                      border: "1px solid #166d4c",
                      px: 1.5,
                      "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", fontWeight: 400, padding: 0 },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Description */}
            {jobOffer?.description && (
              <HTML html={jobOffer.description} />
            )}

            {/* Date and Like */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {postedAgo && (
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Regular, sans-serif",
                    fontSize: "16px",
                    color: "#919191",
                    letterSpacing: "-0.3333px"
                  }}
                >
                  {postedAgo}
                </Typography>
              )}
              <Box
                component="img"
                src={LikeIcon}
                alt="Like"
                onClick={handleLikeClick}
                sx={{ width: "32px", height: "32px", cursor: "pointer" }}
              />
            </Box>
          </Box>

          {/* Advantages Card */}
          {hasAdvantages && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "16px",
                  color: "#1b1b1b",
                  letterSpacing: "0.15px"
                }}
              >
                <FormattedMessage id="landing_page.advantages_title" defaultMessage="Advantages" />
              </Typography>
              <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                {renderAdvantageChips(jobOffer?.offer_advantages?.advantages, "advantages", 0)}
                {renderAdvantageChips(jobOffer?.offer_advantages?.schedules, "schedules", advCount0)}
                {renderAdvantageChips(jobOffer?.offer_advantages?.environments, "environment", advCount1)}
                {renderAdvantageChips(jobOffer?.offer_advantages?.primes, "primes", advCount2)}
              </Box>
            </Box>
          )}

          {/* JobDetails Card - Responsibilities, Profile, Offer */}
          {(jobOffer?.responsibilities || jobOffer?.profile || jobOffer?.offer) && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 2.5
              }}
            >
              {jobOffer?.responsibilities && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                      mb: 1.5
                    }}
                  >
                    <FormattedMessage id="job_offer.responsibilities" defaultMessage="Responsibilities" />
                  </Typography>
                  <HTML html={jobOffer.responsibilities} />
                </Box>
              )}

              {jobOffer?.profile && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                      mb: 1.5
                    }}
                  >
                    <FormattedMessage id="job_offer.profile" defaultMessage="Profile" />
                  </Typography>
                  <HTML html={jobOffer.profile} />
                </Box>
              )}

              {jobOffer?.offer && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                      mb: 1.5
                    }}
                  >
                    <FormattedMessage id="job_offer.offer" defaultMessage="Offer" />
                  </Typography>
                  <HTML html={jobOffer.offer} />
                </Box>
              )}
            </Box>
          )}

          {/* Photos & videos Card */}
          {((jobOffer?.job_offer_images_records?.length > 0) ||
            (jobOffer?.job_offer_videos_records?.length > 0)) && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                p: 2.5,
              }}
            >
              <JobOfferMediaGallery jobOffer={jobOffer} />
            </Box>
          )}

          {/* Skills Card */}
          {(jobOffer?.hard_skills?.length > 0 || jobOffer?.soft_skills?.length > 0 || jobOffer?.language_skills?.length > 0) && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: "12px"
              }}
            >
              {jobOffer?.hard_skills?.length > 0 && (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                    }}
                  >
                    <FormattedMessage id="candidate.profile.skills.hard" defaultMessage="Hard skills" />
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {jobOffer.hard_skills.map((skill, index) => (
                      <SkillView key={`hard-${index}`} value={skill} category="hard" level={false} locale={i18nLocale} />
                    ))}
                  </Box>
                </>
              )}

              {jobOffer?.soft_skills?.length > 0 && (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                    }}
                  >
                    <FormattedMessage id="candidate.profile.skills.soft" defaultMessage="Soft skills" />
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {jobOffer.soft_skills.map((skill, index) => (
                      <SkillView key={`soft-${index}`} value={skill} category="soft" level={false} locale={i18nLocale} />
                    ))}
                  </Box>
                </>
              )}

              {jobOffer?.language_skills?.length > 0 && (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontSize: "16px",
                      color: "#1b1b1b",
                      letterSpacing: "0.15px",
                    }}
                  >
                    <FormattedMessage id="candidate.profile.skills.lang" defaultMessage="Language skills" />
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {jobOffer.language_skills.map((skill, index) => (
                      <SkillView key={`lang-${index}`} value={skill} category="lang" level={false} locale={i18nLocale} />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Container>

      <Modal
        hideBackdrop={false}
        open={isApplicationModalOpened}
        onClose={() => setIsApplicationModalOpened(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ display: "flex", alignItems: "center" }}
      >
        <ApplicationDialog
          jobOffer={jobOffer}
          setJobOffer={updateJobOffer}
          jobApplication={candidateApplication}
          setIsApplying={setIsApplicationModalOpened}
        />
      </Modal>
      {isLoginModalOpened && (
        <AuthenticationModal
          isOpen={isLoginModalOpened}
          section="login_or_signin"
          closeModal={() => setIsLoginModalOpened(false)}
          singleJob={true}
          addApplyLogin={loginModalMode === "apply"}
          addFavoriteLogin={loginModalMode === "favorite"}
          customScope={`application_${jobOffer?.id}_S`}
        />
      )}
    </Box>
  );
};

export default PublicJobOfferSummaryDesktop;
